import React, { FC } from 'react';
import { Card } from 'antd';
import {
  calculateAndParseAbsolutDiscountPrice,
  calculateAndParseRelativeDiscountRatio,
  parsePriceWithUnit,
  parseTotalMonthlyPriceWithUnitWithoutCoupon,
  MY_PRICING_FORM_NAME,
  MyPricingFormValues,
  calculateTotalPriceWithoutCoupon,
} from 'src/pages-all/user/user-profile/my-pricing/my-pricing.logic';
import { useSelector } from 'react-redux';
import { getFormValues } from 'redux-form';
import { getElitePaymentRegularPrice } from 'logic/store/elitetutor/elitetutor.selectors';
import { translate, translationKeys } from 'logic/translations/translations.service';
import styled from 'styled-components';
import { colors } from 'styles/colors';
import { useCouponDiscountPrices } from '../hooks/use-coupon-discount-prices';
import { SummaryRow, SummaryValueSpan } from '../styles/summary-styles';
import { CouponDiscount } from './CouponDiscount';

const SummaryCard = styled(Card)<any>``;

const TotalRow = styled(SummaryRow)`
  margin-top: 10px;
  color: ${colors.violetElite};
  font-weight: 700;
  font-size: 20px;
`;

export const SubscriptionSummery: FC = () => {
  const formValues: MyPricingFormValues = useSelector(getFormValues(MY_PRICING_FORM_NAME));
  const regularPrice = useSelector(getElitePaymentRegularPrice);

  const lessonUnitsPerMonth = formValues?.lessonUnitCount;
  const yourTier =
    lessonUnitsPerMonth != null
      ? formValues?.plan?.tiers.find((tier) => tier.endingQuantity >= lessonUnitsPerMonth)
      : undefined;
  const yourPrice = yourTier?.normalizedPrice;

  const totalMonthlyPriceWithUnit = parseTotalMonthlyPriceWithUnitWithoutCoupon(
    lessonUnitsPerMonth,
    regularPrice
  );
  const relativeDiscountPriceWithUnit = calculateAndParseRelativeDiscountRatio(
    yourPrice,
    regularPrice
  );
  const absolutDiscountPriceWithUnit = calculateAndParseAbsolutDiscountPrice(
    lessonUnitsPerMonth,
    yourPrice,
    regularPrice
  );
  const totalPriceWithoutCoupon = calculateTotalPriceWithoutCoupon(lessonUnitsPerMonth, yourPrice);
  const totalPriceWithUnitWithoutCoupon = parsePriceWithUnit(
    totalPriceWithoutCoupon,
    yourPrice?.currency
  );
  const {
    absoluteNegativeCouponDiscountWithUnit,
    priceAfterCouponDiscountWithUnit,
    couponDiscountText,
  } = useCouponDiscountPrices(totalPriceWithoutCoupon, yourPrice);

  return (
    <SummaryCard>
      <SummaryRow>
        <span>{translate(translationKeys.pricing.selection.summary.lessonUnitsPerMonth)}</span>
        <SummaryValueSpan>{lessonUnitsPerMonth}</SummaryValueSpan>
      </SummaryRow>
      <hr />
      <SummaryRow>
        <span>{translate(translationKeys.pricing.selection.summary.regularPricePerMonth)}</span>
        <SummaryValueSpan style={{ textDecoration: 'line-through' }}>
          {totalMonthlyPriceWithUnit}
        </SummaryValueSpan>
      </SummaryRow>
      <SummaryRow style={{ color: colors.green, fontWeight: 700, fontSize: 18 }}>
        <span>
          {translate(translationKeys.pricing.selection.summary.discount)}{' '}
          {relativeDiscountPriceWithUnit}
        </span>
        <SummaryValueSpan>{absolutDiscountPriceWithUnit}</SummaryValueSpan>
      </SummaryRow>
      <CouponDiscount
        couponDiscountText={couponDiscountText}
        absoluteNegativeCouponDiscountWithUnit={absoluteNegativeCouponDiscountWithUnit}
      />
      <TotalRow>
        <span>{translate(translationKeys.pricing.selection.summary.total)}</span>
        <SummaryValueSpan>
          {priceAfterCouponDiscountWithUnit ?? totalPriceWithUnitWithoutCoupon}
        </SummaryValueSpan>
      </TotalRow>
    </SummaryCard>
  );
};
